/**
 * @file useConversationRead.ts
 * @description Отметка «прочитано» открытого диалога (chat-window-design.md
 * §6; roles.md R4.19; cabinet-api-contract §12 `POST …/mark-read`).
 *
 * Отметка личная: сервер запоминает, что этот сотрудник открыл диалог и
 * прочитал до самого позднего сообщения. Она гасит мои непрочитанные и
 * пометки «Передан вам» / «Из архива» — кабинет у себя «открыл» не хранит, а
 * после ответа перечитывает карточку и берёт признаки как есть.
 *
 * Слать — только когда диалог на экране, окно открыто, вкладка браузера
 * активна (`visible`) и карточка разрешает (`actions.markRead`: при входе
 * под сотрудником — никогда). Повод: есть мои непрочитанные, горит пометка
 * или пока диалог на экране пришло сообщение клиента (`customerTick`).
 */

import { useEffect, useRef } from "react";

import { adminChat, type ConversationDetail } from "../../lib/api-chat";

interface Options {
  id: string | null;
  detail: ConversationDetail | null;
  visible: boolean;
  /** Растёт, когда опрос принёс сообщение клиента. */
  customerTick: number;
  refreshDetail: () => Promise<void>;
  onRead: () => void;
}

/** Есть, что гасить отметкой: мои непрочитанные или пометка строки. */
export function needsMarkRead(detail: ConversationDetail): boolean {
  return detail.unreadCount > 0 || detail.flags.transferredToMe || detail.flags.unarchivedUnseen;
}

export function useConversationRead({ id, detail, visible, customerTick, refreshDetail, onRead }: Options): void {
  const ready = detail !== null && detail.id === id;
  const canMark = ready && visible && detail.actions.markRead;
  const wants = ready && needsMarkRead(detail);
  /** Последний `customerTick`, уже покрытый отметкой. */
  const markedTick = useRef(customerTick);
  const refreshRef = useRef(refreshDetail);
  refreshRef.current = refreshDetail;
  const onReadRef = useRef(onRead);
  onReadRef.current = onRead;

  useEffect(() => {
    if (!id || !canMark) return;
    if (!wants && customerTick === markedTick.current) return;
    markedTick.current = customerTick;
    let alive = true;
    adminChat
      .markRead(id)
      .then(async () => {
        if (!alive) return;
        // Признаки и счётчик — по ответу сервера, а не по догадке кабинета.
        await refreshRef.current();
        onReadRef.current();
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [id, canMark, wants, customerTick]);
}
